import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '$store/rootReducer';

import {
  BalanceState,
  BalanceSetLoadedAction,
  SetInvoiceUrlAction,
  GenerateInvoiceAction,
  WithdrawAction,
  SetBalanceAction,
  GetAddressAction,
  SetBscAddressAction,
  SetTopUpActiveAction,
  TopUpAction,
  PayAction,
  SetEurBalanceAction,
  SetRatesAction,
  TopUpFromTonBalanceAction,
  SetTipsDataAction,
  SetIsTipsPaidAction,
  SetBattleDonateDataAction,
  SetOperationsAction,
} from './interface';
import { CryptoPayTopUpAction, SetCryptoPayUrlAction } from '$store/pay/interface';
import { BalancePayAction } from '$store/balancePay/interface';

const initialState: BalanceState = {
  action: null,
  balance: '0',
  eurBalance: '0',
  operations: [],
  isLoading: true,
  isFailed: false,
  invoiceUrl: '',
  withdrawals: [],
  isModel: false,
  bscAddress: null,
  likesCount: 0,
  likePrice: 0,
  isGold: false,
  topUpActive: false,
  topUpTonActive: false,
  requireTopUpAmount: 0,
  requireTopUpTonAmount: 0,
  loadingRates: false,
  rates: {
    ton: {},
    eur: {},
  },
  topUp: {
    amount: 0,
    skipAutoPay: false,
  },
  holdBalance: '0',
  tonHoldBalance: '0',
  holdDays: 21,
  cryptoPayUrl: null,
  tipsData: null,
  isTipsPaid: false,
  isBattleDonatePaid: false,
  battleDonateData: null,
};

export const { actions, reducer } = createSlice({
  name: 'balance',
  initialState,
  reducers: {
    loadBalance(state) {
      state.isLoading = true;
      state.isFailed = false;
    },
    setLoaded(state, action: BalanceSetLoadedAction) {
      const {
        balance,
        eurBalance,
        operations,
        withdrawals,
        isModel,
        bscAddress,
        likesCount,
        likePrice,
      } = action.payload;

      state.isLoading = false;
      state.balance = balance;
      state.eurBalance = eurBalance;
      state.operations = operations;
      state.withdrawals = withdrawals;
      state.isModel = isModel;
      state.bscAddress = bscAddress;
      state.likesCount = likesCount;
      state.likePrice = likePrice;
    },
    setFailed(state) {
      state.isFailed = true;
      state.isLoading = false;
    },
    generateInvoice(_, __: GenerateInvoiceAction) {},
    setInvoiceUrl(state, action: SetInvoiceUrlAction) {
      state.invoiceUrl = action.payload.invoiceUrl;
    },
    withdraw(_, __: WithdrawAction) {},
    setBalance(state, action: SetBalanceAction) {
      state.balance = action.payload;
    },
    setHoldBalance(state, action: SetBalanceAction) {
      state.holdBalance = action.payload;
    },
    setTonHoldBalance(state, action: SetBalanceAction) {
      state.tonHoldBalance = action.payload;
    },
    setHoldDays(state, action: PayloadAction<number>) {
      state.holdDays = action.payload;
    },
    setOperations(state, action: SetOperationsAction) {
      state.operations = action.payload;
    },
    setEurBalance(state, action: SetEurBalanceAction) {
      state.eurBalance = action.payload;
    },
    getAddress(_, __: GetAddressAction) {},
    setBscAddress(state, action: SetBscAddressAction) {
      state.bscAddress = action.payload;
    },
    topUpFromTonBalance(_, action: TopUpFromTonBalanceAction) {},
    topUpFromTonBalanceWithContext(_, action: TopUpFromTonBalanceAction) {},
    setTopUpActive(state, action: SetTopUpActiveAction) {
      state.topUpActive = action.payload.topUpActive;
      state.requireTopUpAmount = action.payload.requireTopUpAmount || 0;
      if (!action.payload.topUpActive) {
        state.cryptoPayUrl = null;
      }
    },
    setTopUpTonActive(state, action: SetTopUpActiveAction) {
      state.topUpTonActive = action.payload.topUpActive;
      state.requireTopUpTonAmount = action.payload.requireTopUpAmount || 0;
    },
    setIsGold(state, action: PayloadAction<boolean>) {
      state.isGold = action.payload;
    },
    topUp(state, action: TopUpAction) {
      state.topUp = action.payload;
      state.action = null;
    },
    topUpRetry() {},
    pay(state, action: PayAction) {
      state.action = action.payload.action;
    },
    setLoadingRates(state, action: PayloadAction<boolean>) {
      state.loadingRates = action.payload;
    },
    loadTonRate() {},
    setRates(state, action: SetRatesAction) {
      state.rates = action.payload;
    },
    cryptoBayTopUp(_, action: CryptoPayTopUpAction) {},
    setCryptoPayUrl(state, action: SetCryptoPayUrlAction) {
      state.cryptoPayUrl = action.payload;
    },
    updateAllBalances() {},
    setTipsData(state, action: SetTipsDataAction) {
      state.tipsData = action.payload;
    },
    setIsTipsPaid(state, action: SetIsTipsPaidAction) {
      state.isTipsPaid = action.payload;
    },
    setBattleDonateData(state, action: SetBattleDonateDataAction) {
      state.battleDonateData = action.payload;
    },
    setIsBattleDonatePaid(state, action: SetIsTipsPaidAction) {
      state.isBattleDonatePaid = action.payload;
    },
    startCheckTonBalanceTopUp() {},
    stopCheckTonBalanceTopUp() {},
    attachCard(state) {
      state.action = BalancePayAction.ATTACH_CARD;
    },
  },
});

export { reducer as balanceReducer, actions as balanceActions };

export const balanceSelector = (state: RootState) => state.balance;
